import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Sizes } from '@/components/ui/types';

import {
  CardActionsProps,
  CardBodyProps,
  CardFigureProps,
  CardLayout,
  CardProps,
  CardTitleProps,
  CardVariant,
} from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'card-xs',
  sm: 'card-sm',
  md: 'card-md',
  lg: 'card-lg',
  xl: 'card-xl',
};

const variantClasses: Record<CardVariant, string> = {
  default: '',
  border: 'card-border',
  dash: 'card-dash',
};

const layoutClasses: Record<CardLayout, string> = {
  default: '',
  side: 'card-side',
};

const CardBase = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      size = 'md',
      variant = 'default',
      layout = 'default',
      imageFull = false,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'card',
      sizeClasses[size],
      variantClasses[variant],
      layoutClasses[layout],
      imageFull && 'image-full',
      className,
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  },
);

const Body = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('card-body', className)} {...props}>
      {children}
    </div>
  ),
);

const Title = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => (
    <h2 ref={ref} className={twMerge('card-title', className)} {...props}>
      {children}
    </h2>
  ),
);

const Actions = forwardRef<HTMLDivElement, CardActionsProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('card-actions', className)} {...props}>
      {children}
    </div>
  ),
);

const Figure = forwardRef<HTMLElement, CardFigureProps>(
  ({ className, children, ...props }, ref) => (
    <figure ref={ref} className={twMerge(className)} {...props}>
      {children}
    </figure>
  ),
);

CardBase.displayName = 'Card';
Body.displayName = 'Card.Body';
Title.displayName = 'Card.Title';
Actions.displayName = 'Card.Actions';
Figure.displayName = 'Card.Figure';

export const Card = Object.assign(CardBase, {
  Body,
  Title,
  Actions,
  Figure,
});
